import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";

import {
  ModerationReviewResource,
  UploadSuspension,
} from "../../api/moderation";

interface SuspendUploadsDialogProps {
  /** The member to act on. `undefined` keeps the dialog closed. */
  userId?: string;
  onClose: () => void;
  onDone: (message: string) => void;
}

/** Offered durations. `indefinite` sends no expiry, which is what the API reads as one. */
const DURATIONS = [
  { value: "7", label: "7 days" },
  { value: "30", label: "30 days" },
  { value: "90", label: "90 days" },
  { value: "indefinite", label: "Indefinite" },
];

function expiryFor(duration: string): string | undefined {
  if (duration === "indefinite") return undefined;
  const days = Number(duration);
  const at = new Date(Date.now() + days * 24 * 60 * 60 * 1000);
  // The API normalises whatever it is sent, but sending UTC keeps what the
  // moderator sees here and what is stored the same string.
  return at.toISOString();
}

/**
 * Bars a member from uploading a picture, or lifts an existing bar.
 *
 * Reads the current state first rather than assuming: a member may already be
 * suspended by someone else, and imposing a new one silently replaces it.
 */
export const SuspendUploadsDialog: React.FC<SuspendUploadsDialogProps> = ({
  userId,
  onClose,
  onDone,
}) => {
  const [current, setCurrent] = useState<UploadSuspension | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [duration, setDuration] = useState("30");

  useEffect(() => {
    // Cleared before the read, not just on success. Without this the dialog
    // keeps whatever it last read: open it on one member, cancel, open it on
    // another whose read then fails, and the first member's terms — their
    // moderator, their reason — render under the second member's name, with a
    // "Lift suspension" button that would act on the wrong person.
    setCurrent(null);
    setReason("");
    setDuration("30");
    setError(null);

    if (!userId) return;

    setLoading(true);
    ModerationReviewResource.getSuspension(userId)
      .then(setCurrent)
      .catch((err: any) =>
        setError(err?.message || "Could not read the current suspension")
      )
      .finally(() => setLoading(false));
  }, [userId]);

  const suspend = async () => {
    if (!userId) return;
    setSaving(true);
    setError(null);
    try {
      await ModerationReviewResource.suspendUploads(userId, {
        reason: reason.trim() || undefined,
        expiresAt: expiryFor(duration),
      });
      onDone("Uploads suspended for this member");
    } catch (err: any) {
      setError(err?.message || "Could not suspend uploads");
    } finally {
      setSaving(false);
    }
  };

  const lift = async () => {
    if (!userId) return;
    setSaving(true);
    setError(null);
    try {
      await ModerationReviewResource.liftSuspension(userId);
      onDone("Suspension lifted");
    } catch (err: any) {
      setError(err?.message || "Could not lift the suspension");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!userId} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Upload privileges</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {error && <Alert severity="error">{error}</Alert>}

            {!current && error ? (
              // The read failed, so the member's state is unknown. Saying "this
              // member can upload" here would be asserting something nobody
              // established.
              <Alert severity="warning">
                Could not read this member's current state. Suspending will
                still work, and replaces any suspension already in place.
              </Alert>
            ) : current?.suspended ? (
              <Alert severity="warning">
                Already suspended
                {current.suspendedBy ? ` by ${current.suspendedBy}` : ""}
                {current.expiresAt
                  ? `, until ${new Date(current.expiresAt).toLocaleString()}`
                  : ", indefinitely"}
                {current.reason ? ` — "${current.reason}"` : ""}.
                <br />
                Suspending again replaces these terms.
              </Alert>
            ) : (
              <Typography variant="body2" color="text.secondary">
                This member can upload a picture. Suspending stops them until it
                lifts; they are told when they next try.
              </Typography>
            )}

            <TextField
              select
              label="Duration"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              size="small"
              fullWidth
            >
              {DURATIONS.map((d) => (
                <MenuItem key={d.value} value={d.value}>
                  {d.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Reason"
              placeholder="Recorded for the audit trail; shown to no member"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              size="small"
              fullWidth
              multiline
              minRows={2}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        {current?.suspended && (
          <Button onClick={lift} disabled={saving || loading} color="primary">
            Lift suspension
          </Button>
        )}
        <Button
          onClick={suspend}
          disabled={saving || loading}
          color="warning"
          variant="contained"
        >
          {current?.suspended ? "Replace" : "Suspend uploads"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
